import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from 'react';
import { TOP_NAV_HEIGHT } from '@/app/constants/top-navigation';
import { useMediaQuery } from '@/app/hooks/useMediaQuery';
const SPACING_TOP = 44;
const SPACING_SIDE = 16;
const DRAG_THRESHOLD = 5;
interface Point {
  x: number;
  y: number;
}
interface Bounds {
  minX: number;
  minY: number;
}
const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));
const getBounds = (element: HTMLElement): Bounds => {
  const parent = element.offsetParent;
  let parentLeft = 0;
  let parentTop = 0;
  if (parent) {
    const parentRect = parent.getBoundingClientRect();
    parentLeft = parentRect.left;
    parentTop = parentRect.top;
  }
  return {
    minX: SPACING_SIDE - (parentLeft + element.offsetLeft),
    minY: TOP_NAV_HEIGHT + SPACING_TOP - (parentTop + element.offsetTop)
  };
};
const clampPoint = (point: Point, bounds: Bounds): Point => ({
  x: clamp(point.x, bounds.minX, 0),
  y: clamp(point.y, bounds.minY, 0)
});
const applyTransform = (element: HTMLElement, point: Point) => {
  element.style.transform = `translate(${point.x}px, ${point.y}px)`;
};
export const useStickyMenuDrag = () => {
  const $ = _c(9);
  const isMobile = useMediaQuery("(max-width: 1023px)");
  const [isDragging, setIsDragging] = useState(false);
  const toggleRef = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      x: 0,
      y: 0
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const position = useRef(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      x: 0,
      y: 0
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const touchStart = useRef(t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      x: 0,
      y: 0
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const dragStart = useRef(t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      minX: 0,
      minY: 0
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const bounds = useRef(t3);
  const isDraggingRef = useRef(false);
  const hasDragged = useRef(false);
  const animationFrameId = useRef(null);
  let t4;
  let t5;
  if ($[4] !== isMobile) {
    t4 = () => {
      const element = toggleRef.current;
      if (!element) {
        return;
      }
      position.current = {
        x: 0,
        y: 0
      };
      applyTransform(element, position.current);
      if (!isMobile) {
        return;
      }
      const cancelFrame = () => {
        if (animationFrameId.current !== null) {
          cancelAnimationFrame(animationFrameId.current);
          animationFrameId.current = null;
        }
      };
      const handleTouchStart = event => {
        touchStart.current = {
          x: event.touches[0].clientX,
          y: event.touches[0].clientY
        };
        dragStart.current = position.current;
        bounds.current = getBounds(element);
        isDraggingRef.current = true;
        hasDragged.current = false;
        setIsDragging(true);
      };
      const handleTouchMove = event_0 => {
        if (event_0.touches.length !== 1 || !isDraggingRef.current) {
          return;
        }
        if (event_0.cancelable) {
          event_0.preventDefault();
        }
        const deltaX = event_0.touches[0].clientX - touchStart.current.x;
        const deltaY = event_0.touches[0].clientY - touchStart.current.y;
        if (Math.abs(deltaX) > DRAG_THRESHOLD || Math.abs(deltaY) > DRAG_THRESHOLD) {
          hasDragged.current = true;
        }
        position.current = clampPoint({
          x: dragStart.current.x + deltaX,
          y: dragStart.current.y + deltaY
        }, bounds.current);
        cancelFrame();
        animationFrameId.current = requestAnimationFrame(() => {
          applyTransform(element, position.current);
        });
      };
      const handleTouchEnd = () => {
        isDraggingRef.current = false;
        setIsDragging(false);
        cancelFrame();
        applyTransform(element, position.current);
      };
      const handleResize = () => {
        position.current = clampPoint(position.current, getBounds(element));
        applyTransform(element, position.current);
      };
      const handleClick = event_1 => {
        if (hasDragged.current) {
          event_1.preventDefault();
          event_1.stopPropagation();
        }
      };
      element.addEventListener("touchstart", handleTouchStart, {
        passive: true
      });
      element.addEventListener("touchmove", handleTouchMove, {
        passive: false
      });
      element.addEventListener("touchend", handleTouchEnd, {
        passive: true
      });
      element.addEventListener("touchcancel", handleTouchEnd, {
        passive: true
      });
      element.addEventListener("click", handleClick, {
        capture: true
      });
      window.addEventListener("resize", handleResize);
      return () => {
        cancelFrame();
        element.removeEventListener("touchstart", handleTouchStart);
        element.removeEventListener("touchmove", handleTouchMove);
        element.removeEventListener("touchend", handleTouchEnd);
        element.removeEventListener("touchcancel", handleTouchEnd);
        element.removeEventListener("click", handleClick, {
          capture: true
        } as EventListenerOptions);
        window.removeEventListener("resize", handleResize);
      };
    };
    t5 = [isMobile];
    $[4] = isMobile;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
  const t6 = isMobile && isDragging;
  let t7;
  if ($[7] !== t6) {
    t7 = {
      isDragging: t6,
      toggleRef
    };
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  return t7;
};